import { normalizeId } from './helpers';

// Label enrichment helpers - all check both before/after labels
export const getAccreditationLabel = (accreditationId, labels) => {
  if (!labels) return null;
  const beforeLabels = labels.before || labels;
  const afterLabels = labels.after || labels;
  const targetId = normalizeId(accreditationId);
  if (targetId === null) return null;

  for (const l of [beforeLabels, afterLabels]) {
    if (l?.accreditations?.length) {
      const found = l.accreditations.find(
        (a) => normalizeId(a.id) === targetId,
      );
      if (found?.displayName) return found.displayName;
    }
  }
  return null;
};

export const getExtraLabel = (extraId, labels) => {
  if (!labels) return null;
  const beforeLabels = labels.before || labels;
  const afterLabels = labels.after || labels;
  const targetId = normalizeId(extraId);
  if (targetId === null) return null;

  for (const l of [beforeLabels, afterLabels]) {
    if (l?.extras?.length) {
      const found = l.extras.find((e) => normalizeId(e.id) === targetId);
      if (found?.name) return found.name;
    }
  }
  return null;
};

export const getThemeLabel = (themeId, labels) => {
  if (!labels) return null;
  const beforeLabels = labels.before || labels;
  const afterLabels = labels.after || labels;
  const targetId = normalizeId(themeId);
  if (targetId === null) return null;

  for (const l of [beforeLabels, afterLabels]) {
    if (l?.themes?.length) {
      const found = l.themes.find((t) => normalizeId(t.id) === targetId);
      if (found?.displayName) return found.displayName;
    }
  }
  return null;
};

export const getSegmentLabel = (segmentId, labels) => {
  if (!labels) return null;
  const beforeLabels = labels.before || labels;
  const afterLabels = labels.after || labels;
  const targetId = normalizeId(segmentId);
  if (targetId === null) return null;

  for (const l of [beforeLabels, afterLabels]) {
    if (l?.segments?.length) {
      const found = l.segments.find((s) => normalizeId(s.id) === targetId);
      if (found?.displayName) return found.displayName;
    }
  }
  return null;
};

export const getProgramLabel = (programId, labels) => {
  if (!labels) return null;
  const beforeLabels = labels.before || labels;
  const afterLabels = labels.after || labels;
  const targetId = normalizeId(programId);
  if (targetId === null) return null;

  for (const l of [beforeLabels, afterLabels]) {
    if (l?.programs?.length) {
      const found = l.programs.find((p) => normalizeId(p.id) === targetId);
      if (found) return found;
    }
  }
  return null;
};

export const getTransferLabel = (transferId, labels) => {
  if (!labels) return null;
  const beforeLabels = labels.before || labels;
  const afterLabels = labels.after || labels;
  const targetId = normalizeId(transferId);
  if (targetId === null) return null;

  for (const l of [beforeLabels, afterLabels]) {
    if (l?.transfers?.length) {
      const found = l.transfers.find((t) => normalizeId(t.id) === targetId);
      if (found) return found;
    }
  }
  return null;
};

export const getExtraWithDependenciesLabel = (extraWithDepsId, labels) => {
  if (!labels) return null;
  const beforeLabels = labels.before || labels;
  const afterLabels = labels.after || labels;
  const targetId = normalizeId(extraWithDepsId);
  if (targetId === null) return null;

  for (const l of [beforeLabels, afterLabels]) {
    if (l?.extrasWithDependencies?.length) {
      const found = l.extrasWithDependencies.find(
        (e) => normalizeId(e.id) === targetId,
      );
      if (found) return found;
    }
  }
  return null;
};

export const getPositionLabel = (positionId, labels, programId = null) => {
  if (!labels) return null;
  const targetId = normalizeId(positionId);
  if (targetId === null) return null;

  // If programId is provided, look in program-specific positions first
  if (programId) {
    const programLabel = getProgramLabel(programId, labels);
    if (programLabel) {
      if (
        programLabel.startPosition &&
        normalizeId(programLabel.startPosition.id) === targetId
      ) {
        return programLabel.startPosition.name;
      }
      if (
        programLabel.endPosition &&
        normalizeId(programLabel.endPosition.id) === targetId
      ) {
        return programLabel.endPosition.name;
      }
    }
  }
  // Check top-level positions
  if (
    labels.startPosition &&
    normalizeId(labels.startPosition.id) === targetId
  ) {
    return labels.startPosition.name;
  }
  if (labels.endPosition && normalizeId(labels.endPosition.id) === targetId) {
    return labels.endPosition.name;
  }
  return null;
};

// Get accommodation hotel label by searching through all program labels
export const getAccommodationHotelLabel = (hotelId, labels) => {
  if (!labels) return null;
  const beforeLabels = labels.before || labels;
  const afterLabels = labels.after || labels;
  const targetId = normalizeId(hotelId);
  if (targetId === null) return null;

  for (const l of [beforeLabels, afterLabels]) {
    if (l?.programs?.length) {
      for (const program of l.programs) {
        if (
          program.accomodationHotel &&
          normalizeId(program.accomodationHotel.id) === targetId
        ) {
          return program.accomodationHotel.displayName;
        }
      }
    }
  }
  return null;
};

// Get accommodation experience label by searching through all program labels
export const getAccommodationExperienceLabel = (experienceId, labels) => {
  if (!labels) return null;
  const beforeLabels = labels.before || labels;
  const afterLabels = labels.after || labels;
  const targetId = normalizeId(experienceId);
  if (targetId === null) return null;

  for (const l of [beforeLabels, afterLabels]) {
    if (l?.programs?.length) {
      for (const program of l.programs) {
        if (
          program.accomodationExperience &&
          normalizeId(program.accomodationExperience.id) === targetId
        ) {
          return program.accomodationExperience.name;
        }
      }
    }
  }
  return null;
};

// Enrich primitive value based on field name
// isBefore: true for before value, false for after value (used for inCityId which has no ID in labels)
export const getEnrichedValue = (
  value,
  fieldName,
  labels,
  isBefore = false,
) => {
  if (!labels || value === null || value === undefined) return null;

  const beforeLabels = labels.before || labels;
  const afterLabels = labels.after || labels;
  const targetId = normalizeId(value);

  switch (fieldName) {
    case 'inCityId':
      // Labels only have inCityName, not inCityId, so use isBefore to determine which to show
      if (isBefore) {
        return beforeLabels.inCityName || null;
      }
      return afterLabels.inCityName || labels.inCityName || null;
    case 'startPositionId':
      // Check before labels
      if (
        beforeLabels.startPosition?.id &&
        normalizeId(beforeLabels.startPosition.id) === targetId
      ) {
        return beforeLabels.startPosition.name;
      }
      // Check after labels
      if (
        afterLabels.startPosition?.id &&
        normalizeId(afterLabels.startPosition.id) === targetId
      ) {
        return afterLabels.startPosition.name;
      }
      return null;
    case 'endPositionId':
      // Check before labels
      if (
        beforeLabels.endPosition?.id &&
        normalizeId(beforeLabels.endPosition.id) === targetId
      ) {
        return beforeLabels.endPosition.name;
      }
      // Check after labels
      if (
        afterLabels.endPosition?.id &&
        normalizeId(afterLabels.endPosition.id) === targetId
      ) {
        return afterLabels.endPosition.name;
      }
      return null;
    case 'accomodationHotelId':
      return getAccommodationHotelLabel(value, labels);
    case 'accomodationExperienceId':
      return getAccommodationExperienceLabel(value, labels);
    default:
      return null;
  }
};

// Get resolved attachment from attachment labels
export const getResolvedAttachment = (attachment, attachmentLabels) => {
  if (!attachment?.id || !attachmentLabels?.length) return null;
  const targetId = String(attachment.id);
  return attachmentLabels.find((a) => String(a.id) === targetId);
};

// Collect all attachment labels from labels structure
export const getAllAttachmentLabels = (labels) => {
  if (!labels) return [];
  const allAttachments = [];

  // Collect from before/after structure
  const beforeLabels = labels.before || labels;
  const afterLabels = labels.after || labels;

  [beforeLabels, afterLabels].forEach((l) => {
    if (l?.attachments?.length) {
      allAttachments.push(...l.attachments);
    }
    if (l?.programs?.length) {
      l.programs.forEach((program) => {
        if (program.attachments?.length) {
          allAttachments.push(...program.attachments);
        }
      });
    }
  });

  // Dedupe by id
  const seen = new Set();
  return allAttachments.filter((a) => {
    if (seen.has(String(a.id))) return false;
    seen.add(String(a.id));
    return true;
  });
};

// Get program title from program object and labels
export const getProgramTitle = (program, labels) => {
  const programLabel = getProgramLabel(program?.id, labels);
  const translation = program?.translations?.[0];
  return programLabel?.title || translation?.title || `Program #${program?.id}`;
};

// Get alternative hotel label by searching through all program labels and requestHotels
export const getAlternativeHotelLabel = (hotelId, labels) => {
  if (!labels) return null;
  const beforeLabels = labels.before || labels;
  const afterLabels = labels.after || labels;
  const targetId = normalizeId(hotelId);
  if (targetId === null) return null;

  for (const l of [beforeLabels, afterLabels]) {
    if (l?.programs?.length) {
      for (const program of l.programs) {
        if (program.alternativeHotels?.length) {
          const found = program.alternativeHotels.find(
            (h) => normalizeId(h.hotelId) === targetId,
          );
          if (found?.hotelName) return found.hotelName;
        }
      }
    }
    // Also check requestHotels labels
    if (l?.requestHotels?.length) {
      const found = l.requestHotels.find(
        (h) => normalizeId(h.hotelId) === targetId,
      );
      if (found?.hotelName) return found.hotelName;
    }
  }
  return null;
};

// Get program experience label by searching through all program labels and requestExperiences
export const getProgramExperienceLabel = (experienceId, labels) => {
  if (!labels) return null;
  const beforeLabels = labels.before || labels;
  const afterLabels = labels.after || labels;
  const targetId = normalizeId(experienceId);
  if (targetId === null) return null;

  for (const l of [beforeLabels, afterLabels]) {
    if (l?.programs?.length) {
      for (const program of l.programs) {
        // Check programExperiences
        if (program.programExperiences?.length) {
          const found = program.programExperiences.find(
            (e) => normalizeId(e.experienceId) === targetId,
          );
          if (found?.experienceName) return found.experienceName;
        }
        // Check includedOptions
        if (program.includedOptions?.length) {
          const found = program.includedOptions.find(
            (e) => normalizeId(e.experienceId) === targetId,
          );
          if (found?.experienceName) return found.experienceName;
        }
        // Check suggestedOptions
        if (program.suggestedOptions?.length) {
          const found = program.suggestedOptions.find(
            (e) => normalizeId(e.experienceId) === targetId,
          );
          if (found?.experienceName) return found.experienceName;
        }
      }
    }
    // Also check requestExperiences labels
    if (l?.requestExperiences?.length) {
      const found = l.requestExperiences.find(
        (e) => normalizeId(e.experienceId) === targetId,
      );
      if (found?.experienceName) return found.experienceName;
    }
  }
  return null;
};
